import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, LOGO } from "@/lib/config";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { MapPin, Package, Users, DollarSign, Trash2, Plus, LogOut, Power, Loader2, MessageSquare, Check, Star, CalendarDays } from "lucide-react";
import { ScheduleManager } from "@/components/ScheduleManager";

const StatCard = ({ icon: Icon, label, value, tid }) => (
  <div className="rounded-2xl bg-white border border-slate-200 p-5 shadow-sm" data-testid={tid}>
    <div className="flex items-center justify-between">
      <span className="text-xs font-bold tracking-wide text-slate-500 uppercase">{label}</span>
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-boco-greenlight text-boco-greendark"><Icon className="h-4 w-4" /></div>
    </div>
    <div className="mt-3 font-display text-3xl font-black text-boco-navy">{value}</div>
  </div>
);

export default function AdminDashboard() {
  const { user, ready, logout } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [areas, setAreas] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [leads, setLeads] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [pwInquiries, setPwInquiries] = useState([]);
  const [newArea, setNewArea] = useState({ zip: "", city: "", county: "", state: "VA" });
  const [adding, setAdding] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [s, a, b, l, rv, pw] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/service-areas"),
        api.get("/admin/bookings"), api.get("/admin/leads"), api.get("/admin/reviews"),
        api.get("/admin/pressure-washing"),
      ]);
      setStats(s.data); setAreas(a.data); setBookings(b.data); setLeads(l.data); setReviews(rv.data); setPwInquiries(pw.data);
    } catch (e) {
      if (e.response?.status === 401) navigate("/admin/login");
    }
  }, [navigate]);

  useEffect(() => {
    if (!ready) return;
    if (!user) { navigate("/admin/login"); return; }
    loadAll();
  }, [ready, user, navigate, loadAll]);

  const addArea = async (e) => {
    e.preventDefault();
    if (newArea.zip.replace(/\D/g, "").length !== 5 || !newArea.city) { toast.error("Enter a 5-digit ZIP and city"); return; }
    setAdding(true);
    try {
      await api.post("/admin/service-areas", newArea);
      toast.success(`Added ${newArea.city} (${newArea.zip})`);
      setNewArea({ zip: "", city: "", county: "", state: "VA" });
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not add ZIP"); }
    finally { setAdding(false); }
  };

  const toggleArea = async (id) => { await api.patch(`/admin/service-areas/${id}`); loadAll(); };
  const deleteArea = async (id) => { await api.delete(`/admin/service-areas/${id}`); toast.success("Removed"); loadAll(); };
  const toggleReview = async (id) => { await api.patch(`/admin/reviews/${id}`); loadAll(); };
  const deleteReview = async (id) => { await api.delete(`/admin/reviews/${id}`); toast.success("Review deleted"); loadAll(); };

  if (!ready || (ready && !user)) {
    return <div className="min-h-screen bg-boco-navy flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-white" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="bg-boco-navy text-white">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="BOCO BINS" className="h-11 w-11 rounded-full object-cover" />
            <div>
              <div className="font-display font-extrabold leading-none">BOCO BINS <span className="text-boco-green">Admin</span></div>
              <div className="text-xs text-slate-400 mt-0.5">{user?.email}</div>
            </div>
          </div>
          <button onClick={() => { logout(); navigate("/admin/login"); }} className="flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-sm font-semibold hover:bg-white/10 transition-colors" data-testid="logout-btn">
            <LogOut className="h-4 w-4" /> Log Out
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 sm:px-8 py-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={DollarSign} label="Revenue (paid)" value={`$${(stats?.revenue || 0).toFixed(0)}`} tid="stat-revenue" />
          <StatCard icon={CalendarDays} label="Upcoming Cleanings" value={stats?.upcoming_appointments ?? 0} tid="stat-upcoming" />
          <StatCard icon={Users} label="Recurring Customers" value={stats?.active_customers ?? 0} tid="stat-customers" />
          <StatCard icon={MessageSquare} label="Pending Reviews" value={stats?.pending_reviews ?? 0} tid="stat-pending-reviews" />
        </div>

        <Tabs defaultValue="schedule" className="mt-8">
          <TabsList className="bg-white border border-slate-200 flex-wrap h-auto">
            <TabsTrigger value="schedule" data-testid="tab-schedule">Schedule</TabsTrigger>
            <TabsTrigger value="areas" data-testid="tab-areas">Service Areas</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="tab-bookings">Bookings</TabsTrigger>
            <TabsTrigger value="leads" data-testid="tab-leads">Expansion Leads</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="tab-reviews">Reviews</TabsTrigger>
            <TabsTrigger value="pressure" data-testid="tab-pressure">Pressure Washing</TabsTrigger>
          </TabsList>

          <TabsContent value="schedule" className="mt-6">
            <ScheduleManager />
          </TabsContent>

          {/* SERVICE AREAS */}
          <TabsContent value="areas" className="mt-6">
            <div className="rounded-2xl bg-white border border-slate-200 p-6 shadow-sm">
              <h3 className="font-display text-lg font-extrabold text-boco-navy">Add a ZIP Code</h3>
              <p className="text-sm text-slate-500">Expand your service area anytime — no code changes needed.</p>
              <form onSubmit={addArea} className="mt-4 grid gap-3 sm:grid-cols-5">
                <Input placeholder="ZIP" value={newArea.zip} onChange={(e) => setNewArea({ ...newArea, zip: e.target.value.replace(/\D/g, "").slice(0, 5) })} data-testid="area-zip-input" />
                <Input placeholder="City / Town" value={newArea.city} onChange={(e) => setNewArea({ ...newArea, city: e.target.value })} data-testid="area-city-input" />
                <Input placeholder="County" value={newArea.county} onChange={(e) => setNewArea({ ...newArea, county: e.target.value })} data-testid="area-county-input" />
                <Input placeholder="State" value={newArea.state} onChange={(e) => setNewArea({ ...newArea, state: e.target.value })} data-testid="area-state-input" />
                <button type="submit" disabled={adding} className="rounded-md bg-boco-green px-4 font-bold text-boco-navy hover:bg-boco-greendark hover:text-white transition-colors flex items-center justify-center gap-2 disabled:opacity-70" data-testid="area-add-btn">
                  {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4" /> Add</>}
                </button>
              </form>
            </div>

            <div className="mt-6 rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>ZIP</TableHead><TableHead>City</TableHead><TableHead>County</TableHead>
                  <TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {areas.map((a) => (
                    <TableRow key={a.id} data-testid={`area-row-${a.zip}`}>
                      <TableCell className="font-bold text-boco-navy">{a.zip}</TableCell>
                      <TableCell>{a.city}</TableCell>
                      <TableCell className="text-slate-500">{a.county || "-"}</TableCell>
                      <TableCell>
                        <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${a.active ? "bg-boco-greenlight text-boco-greendark" : "bg-slate-100 text-slate-400"}`}>{a.active ? "Active" : "Paused"}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <button onClick={() => toggleArea(a.id)} title="Toggle active" className="inline-flex mr-2 rounded-md p-2 text-slate-500 hover:bg-slate-100 transition-colors" data-testid={`area-toggle-${a.zip}`}><Power className="h-4 w-4" /></button>
                        <button onClick={() => deleteArea(a.id)} title="Delete" className="inline-flex rounded-md p-2 text-red-500 hover:bg-red-50 transition-colors" data-testid={`area-delete-${a.zip}`}><Trash2 className="h-4 w-4" /></button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {areas.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">No service areas yet.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* BOOKINGS */}
          <TabsContent value="bookings" className="mt-6">
            <div className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Date</TableHead><TableHead>Customer</TableHead><TableHead>Contact</TableHead>
                  <TableHead>Address</TableHead>
                  <TableHead>Plan</TableHead><TableHead>ZIP</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {bookings.map((b) => (
                    <TableRow key={b.id} data-testid={`booking-row-${b.id}`}>
                      <TableCell className="text-slate-500 whitespace-nowrap">{new Date(b.created_at).toLocaleDateString()}</TableCell>
                      <TableCell className="font-semibold text-boco-navy">{b.name}</TableCell>
                      <TableCell className="text-sm text-slate-500">{b.email}<br/>{b.phone}</TableCell>
                      <TableCell className="text-sm text-slate-600 max-w-[200px]">{b.address ? b.address : <span className="text-slate-400 italic">Not provided</span>}</TableCell>
                      <TableCell>{b.plan_label}</TableCell>
                      <TableCell>{b.zip}</TableCell>
                      <TableCell className="font-bold">${b.amount}</TableCell>
                      <TableCell><span className={`rounded-full px-2.5 py-1 text-xs font-bold ${b.payment_status === "paid" ? "bg-boco-greenlight text-boco-greendark" : "bg-amber-50 text-amber-600"}`}>{b.payment_status}</span></TableCell>
                    </TableRow>
                  ))}
                  {bookings.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-8">No bookings yet.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* LEADS */}
          <TabsContent value="leads" className="mt-6">
            <div className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Date</TableHead><TableHead>Name</TableHead><TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead><TableHead>ZIP Requested</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {leads.map((l) => (
                    <TableRow key={l.id} data-testid={`lead-row-${l.id}`}>
                      <TableCell className="text-slate-500 whitespace-nowrap">{new Date(l.created_at).toLocaleDateString()}</TableCell>
                      <TableCell className="font-semibold text-boco-navy">{l.name}</TableCell>
                      <TableCell className="text-slate-500">{l.email}</TableCell>
                      <TableCell className="text-slate-500">{l.phone}</TableCell>
                      <TableCell className="font-bold text-boco-navy">{l.zip}</TableCell>
                    </TableRow>
                  ))}
                  {leads.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">No expansion leads yet.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* REVIEWS */}
          <TabsContent value="reviews" className="mt-6">
            <div className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden">
              <div className="p-6 border-b border-slate-100">
                <h3 className="font-display text-lg font-extrabold text-boco-navy">Customer Reviews</h3>
                <p className="text-sm text-slate-500">Approve a review to publish it on the homepage. Pending reviews are hidden until approved.</p>
              </div>
              <div className="divide-y divide-slate-100">
                {reviews.map((rv) => (
                  <div key={rv.id} className="p-6 flex flex-col sm:flex-row sm:items-start gap-4" data-testid={`admin-review-${rv.id}`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <span className="font-bold text-boco-navy">{rv.name}</span>
                        <span className="flex gap-0.5">{[...Array(rv.rating)].map((_, s) => (<Star key={s} className="h-3.5 w-3.5 fill-boco-green text-boco-green" />))}</span>
                        <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${rv.status === "approved" ? "bg-boco-greenlight text-boco-greendark" : "bg-amber-50 text-amber-600"}`}>{rv.status}</span>
                      </div>
                      <p className="mt-2 text-slate-600 text-sm">"{rv.text}"</p>
                      <p className="mt-1 text-xs text-slate-400">{rv.location || "—"} · {new Date(rv.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button onClick={() => toggleReview(rv.id)} className={`inline-flex items-center gap-1 rounded-full px-4 py-2 text-sm font-bold transition-colors ${rv.status === "approved" ? "bg-slate-100 text-slate-600 hover:bg-slate-200" : "bg-boco-green text-boco-navy hover:bg-boco-greendark hover:text-white"}`} data-testid={`review-toggle-${rv.id}`}>
                        <Check className="h-4 w-4" /> {rv.status === "approved" ? "Unpublish" : "Approve"}
                      </button>
                      <button onClick={() => deleteReview(rv.id)} className="inline-flex rounded-full p-2 text-red-500 hover:bg-red-50 transition-colors" data-testid={`review-delete-${rv.id}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                ))}
                {reviews.length === 0 && <div className="p-8 text-center text-slate-400">No reviews yet.</div>}
              </div>
            </div>
          </TabsContent>

          {/* PRESSURE WASHING */}
          <TabsContent value="pressure" className="mt-6">
            <div className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden">
              <div className="p-6 border-b border-slate-100">
                <h3 className="font-display text-lg font-extrabold text-boco-navy">Pressure Washing Inquiries</h3>
                <p className="text-sm text-slate-500">Quote requests submitted from the Pressure Washing page.</p>
              </div>
              <div className="divide-y divide-slate-100">
                {pwInquiries.map((q) => (
                  <div key={q.id} className="p-6 flex flex-col sm:flex-row sm:items-start gap-4" data-testid={`pw-inquiry-${q.id}`}>
                    <div className="flex-1">
                      <div className="font-bold text-boco-navy">{q.name} · <a href={`tel:${q.phone}`} className="text-boco-greendark">{q.phone}</a></div>
                      <div className="text-sm text-slate-500">{q.email || "—"} · {q.address || "No address"}</div>
                      {q.services?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {q.services.map((s) => (<span key={s} className="rounded-full bg-boco-greenlight text-boco-greendark px-2.5 py-1 text-xs font-bold">{s}</span>))}
                        </div>
                      )}
                      {q.details && <p className="mt-2 text-slate-600 text-sm">{q.details}</p>}
                      <p className="mt-1 text-xs text-slate-400">{new Date(q.created_at).toLocaleString()}</p>
                    </div>
                    <button onClick={async () => { await api.delete(`/admin/pressure-washing/${q.id}`); toast.success("Deleted"); loadAll(); }} className="inline-flex rounded-full p-2 text-red-500 hover:bg-red-50 transition-colors self-start" data-testid={`pw-delete-${q.id}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
                {pwInquiries.length === 0 && <div className="p-8 text-center text-slate-400">No pressure washing inquiries yet.</div>}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
